import {
    useInfiniteQuery,
    useMutation,
    useQuery,
    useQueryClient
} from '@tanstack/react-query';

import {
    add,
    deleteRecord,
    fetch as fetchOne,
    fetchRecords,
    PAGE_SIZE,
    TopLevelDomainsPage,
    update,
} from '../services/TopLevelDomainsService';

import {TopLevelDomain} from '../types';

// FNP-944: records are React Query's job. Selection, paging and keyword stay in
// Redux. The service layer is unchanged - these hooks call the same service
// functions the Redux thunks called.

export const topLevelDomainsKeys = {
    all: ['top-level-domains'] as const,
    lists: () => [...topLevelDomainsKeys.all, 'list'] as const,
    list: (filter: object) => [...topLevelDomainsKeys.lists(), filter] as const,
    details: () => [...topLevelDomainsKeys.all, 'detail'] as const,
    detail: (id: string) => [...topLevelDomainsKeys.details(), id] as const,
};

// FNP-1292 — TOKEN PAGINATION.
//
// ⚠️ `useInfiniteQuery`, never `useQuery`. The previous hook fetched exactly
// one unpaginated page and read as compliant on every other data box — it had
// a query hook, keys and invalidation — while a tenant past the first page
// simply could not see their own top level domains. `listTopLevelDomains` has
// always taken `limit` and `nextToken`; nothing on the server needed to change.
export const useTopLevelDomainsInfiniteQuery = (filter: object = {}) => {
    return useInfiniteQuery({
        // The filter is in the key: omit it and changing the filter silently
        // serves the previous filter's cache.
        queryKey: topLevelDomainsKeys.list(filter),
        queryFn: ({pageParam}: { pageParam: string | null }): Promise<TopLevelDomainsPage> =>
            fetchRecords(filter, PAGE_SIZE, pageParam),
        // ⚠️ MUST return `undefined` at the end of the results. Returning
        // `null`, or the same token again, makes React Query refetch forever.
        getNextPageParam: (lastPage) => {
            if (!lastPage || !lastPage.nextToken) {
                return undefined;
            }
            return lastPage.nextToken;
        },
        initialPageParam: null as string | null,
    });
};

/** The flattened records, for consumers that want a plain list. */
export const useTopLevelDomainsQuery = (filter: object = {}) => {
    const query = useTopLevelDomainsInfiniteQuery(filter);

    // A type guard, not a bare flatMap — a partial page object would otherwise
    // throw inside a render.
    const items: TopLevelDomain[] = (query.data?.pages ?? []).flatMap(
        page => (page && 'items' in page) ? (page.items ?? []) : [],
    ).filter(Boolean);

    return {
        ...query,
        data: items,
    };
};

export const useTopLevelDomainQuery = (topLevelDomainId?: string) => {
    return useQuery({
        queryKey: topLevelDomainsKeys.detail(topLevelDomainId ?? ''),
        queryFn: async () => {
            if (typeof topLevelDomainId === 'undefined') {
                throw new Error('topLevelDomainId is required parameter');
            }
            return await fetchOne(topLevelDomainId);
        },
        enabled: typeof topLevelDomainId !== 'undefined' && topLevelDomainId !== '',
    });
};

export const useCreateTopLevelDomainMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (values: TopLevelDomain) => add(values),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: topLevelDomainsKeys.lists()});
        },
    });
};

export const useUpdateTopLevelDomainMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({
                         id,
                         record
                     }: { id: string, record: TopLevelDomain }) => update(id, record),
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({queryKey: topLevelDomainsKeys.lists()});
            queryClient.invalidateQueries({queryKey: topLevelDomainsKeys.detail(variables.id)});
        },
    });
};

export const useDeleteTopLevelDomainMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: string) => deleteRecord(id),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: topLevelDomainsKeys.lists()});
        },
    });
};
